import React from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { MONTH_NAMES, DAY_NAMES, SLOTS, getMonthDays, getDayClassName, formatDate } from "./constants";

export function CalendarPicker({ currentMonth, currentYear, selectedDate, onDateSelect, onPrevMonth, onNextMonth, canGoPrev }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const { firstDay, daysInMonth } = getMonthDays(currentYear, currentMonth);

  const isSunday = (day) => new Date(currentYear, currentMonth, day).getDay() === 0;
  const isPast = (day) => new Date(currentYear, currentMonth, day) < today;
  const isToday = (day) => { const d = new Date(currentYear, currentMonth, day); return d.toDateString() === today.toDateString(); };

  return (
    <div className="bg-surface border border-border_custom p-6 mb-6 rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onPrevMonth} disabled={!canGoPrev} data-testid="calendar-prev"
          className={`p-2 ${canGoPrev ? "text-emerald_deep hover:bg-bone" : "text-text_muted/30 cursor-not-allowed"} transition-colors rounded-full`}>
          <ChevronLeft size={20} />
        </button>
        <h3 className="font-serif text-lg text-emerald_deep">{MONTH_NAMES[currentMonth]} {currentYear}</h3>
        <button onClick={onNextMonth} data-testid="calendar-next" className="p-2 text-emerald_deep hover:bg-bone transition-colors rounded-full">
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAY_NAMES.map((d) => (
          <div key={d} className={`text-center text-xs uppercase tracking-wider py-2 ${d === "Sun" ? "text-red-400/60" : "text-text_muted"}`}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`}></div>)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const sunday = isSunday(day);
          const past = isPast(day);
          const disabled = sunday || past;
          const selected = selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === currentMonth && selectedDate.getFullYear() === currentYear;
          const todayMark = isToday(day);
          return (
            <button key={`day-${day}`} onClick={() => !disabled && onDateSelect(day)} disabled={disabled}
              data-testid={`calendar-day-${day}`}
              className={`relative py-3 text-center text-sm transition-all duration-200 rounded-lg ${getDayClassName(selected, disabled, sunday)}`}>
              {day}
              {todayMark && !selected && <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-accent rounded-full"></div>}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-text_muted/60 mt-4 text-center">Sundays are unavailable. Slot is subject to confirmation by our team.</p>
    </div>
  );
}

export function TimeSlotPicker({ selectedDate, selectedSlot, onSlotSelect }) {
  return (
    <div className="bg-surface border border-border_custom p-6 mb-6 rounded-xl">
      <h3 className="font-serif text-lg text-emerald_deep mb-4 flex items-center gap-2">
        <Clock size={18} className="text-accent" /> Preferred Slot &mdash; {formatDate(selectedDate)}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {SLOTS.map((slot) => (
          <button key={slot} onClick={() => onSlotSelect(slot)} data-testid={`slot-${slot.replace(/\s/g, "-")}`}
            className={`p-4 border text-center transition-all duration-300 rounded-lg ${
              selectedSlot === slot ? "border-accent bg-accent/10 text-emerald_deep" : "border-border_custom hover:border-accent/40 text-emerald_deep"
            }`}>
            <div className="font-serif text-base">{slot}</div>
            <div className="text-xs text-text_muted mt-1">30 min session</div>
          </button>
        ))}
      </div>
      <div className="bg-accent/5 border border-accent/20 p-3 mt-4 rounded-lg">
        <p className="text-xs text-text_muted text-center">This is a preferred slot. Our team will confirm availability or suggest the nearest available time.</p>
      </div>
    </div>
  );
}
